import { type QueryClient } from "@tanstack/react-query";

import {
  type LabeledOption,
  type ResolvedOptions,
} from "@/shared/components/ResourceView/Filters/FilterDefinition";
import { type AdminService } from "@/shared/domain/admin";
import { type OrgService } from "@/shared/domain/orgs";
import { type PaginatedResponse } from "@/shared/http";

/**
 * How many results the resolvers request per search. Deliberately generous:
 * the widget virtualizes the list and the projected rows are small, so
 * fetching a large page up front is cheap. None of the endpoints rank by
 * relevance: org and agent search return the newest matches first and user
 * search orders by ``user_id``, so the page is the first 100 in that
 * whitelisted sort order, not the 100 closest matches. When the server still
 * has more beyond this cap, the widget shows a "refine your search" hint
 * rather than silently pretending this is the whole set. Kept within the
 * admin search endpoints' ``[1, 200]`` bound.
 */
const SEARCH_LIMIT = 100;

/**
 * Build a {@link FilterOptionsResolver} over a paginated admin search
 * endpoint, mirroring ``createTagOptionsResolver``'s structure and caching
 * semantics (see that module — its public API is unrelated and unchanged by
 * this helper, but the two are kept consistent on purpose).
 *
 * Results are retained in the query cache under ``[cacheKey, search]`` via
 * `ensureQueryData`, so reopening a picker serves the last results instantly
 * instead of refetching. This is the retention layer the picker itself
 * deliberately lacks (its `useQuery` runs with `gcTime: 0` because it cannot
 * see the resolver's identity). `cacheKey` must therefore be distinct per
 * resolver and identify everything `fetchPage` closes over.
 *
 * No explicit `staleTime` — matching `createTagOptionsResolver`'s choice of
 * relying on the app's default `QueryClient` `staleTime` rather than
 * overriding it here.
 *
 * `fetchPage` is wired to TanStack's own context `signal`, not the resolver's
 * outer `signal` argument, for the same reason as `createTagOptionsResolver`:
 * `ensureQueryData` de-dupes concurrent callers of the same cache entry, so a
 * fetch it kicks off can outlive any single caller. Aborting a caller's
 * ephemeral signal must not cancel that shared, durable fetch.
 */
function buildSearchOptionsResolver<T>({
  queryClient,
  cacheKey,
  fetchPage,
  toOption,
}: {
  queryClient: QueryClient;
  cacheKey: string;
  fetchPage: (
    search: string,
    signal: AbortSignal,
  ) => Promise<PaginatedResponse<T>>;
  toOption: (item: T) => LabeledOption;
}): (search: string, signal: AbortSignal) => Promise<ResolvedOptions> {
  return async (search): Promise<ResolvedOptions> => {
    const page = await queryClient.ensureQueryData({
      queryKey: [cacheKey, search],
      queryFn: ({ signal }) => fetchPage(search, signal),
    });
    return {
      options: page.items.map(toOption),
      truncated: page.next_token !== null,
    };
  };
}

/** ``org`` → ``"${name} (${org_id})"`` labeled by org id. */
export function buildOrgOptionResolver(
  queryClient: QueryClient,
  orgService: OrgService,
) {
  return buildSearchOptionsResolver({
    queryClient,
    cacheKey: "admin-org-options",
    fetchPage: (search, signal) =>
      orgService.listOrgs(
        { search: search || undefined, limit: SEARCH_LIMIT },
        { abortSignal: signal },
      ),
    toOption: (org) => ({
      label: `${org.name} (${org.org_id})`,
      value: org.org_id,
    }),
  });
}

/** ``user`` → ``name ? "${name} (${user_id})" : user_id`` labeled by user id. */
export function buildUserOptionResolver(
  queryClient: QueryClient,
  adminService: AdminService,
) {
  return buildSearchOptionsResolver({
    queryClient,
    cacheKey: "admin-user-options",
    // The typed term may match either the user's id or their display name, so
    // it becomes an OR of CONTAINS conditions over both whitelisted columns —
    // the same either-column matching the retired GET `search` param did.
    fetchPage: (search, signal) =>
      adminService.searchAdminUsers(
        {
          condition: search
            ? {
                operator: "OR",
                conditions: [
                  { field: "user_id", comparator: "CONTAINS", value: search },
                  { field: "name", comparator: "CONTAINS", value: search },
                ],
              }
            : undefined,
          limit: SEARCH_LIMIT,
        },
        { abortSignal: signal },
      ),
    toOption: (user) => ({
      label: user.name ? `${user.name} (${user.user_id})` : user.user_id,
      value: user.user_id,
    }),
  });
}

/** ``agent`` → ``"${name} · ${org_id}"`` labeled by agent id. Agent names are
 *  unique only per org and this search is cross-org, so two orgs' "Scout"
 *  agents need the owning org to stay tellable apart. */
export function buildAgentOptionResolver(
  queryClient: QueryClient,
  adminService: AdminService,
) {
  return buildSearchOptionsResolver({
    queryClient,
    cacheKey: "admin-agent-options",
    // Agents are matched on name only — the same single-column matching the
    // retired GET `search` param did.
    fetchPage: (search, signal) =>
      adminService.searchAdminAgents(
        {
          condition: search
            ? { field: "name", comparator: "CONTAINS", value: search }
            : undefined,
          limit: SEARCH_LIMIT,
        },
        { abortSignal: signal },
      ),
    toOption: (agent) => ({
      label: `${agent.name} · ${agent.org_id}`,
      value: agent.agent_id,
    }),
  });
}
